import useEdgeAuthService from './useEdgeAuthService';
import useOidcService from './useOidcService';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { decodeJwtToken } from '../../utils/stringUtils';

export enum LoginType {
  Unknown = 'unknown',
  Oidc = 'cloud',
  Edge = 'edge',
}

export const LOGIN_TYPE_KEY = 'currentLoginType';

export default function useAuthService() {
  const edgeAuth = useEdgeAuthService();
  const oidcAuth = useOidcService();
  const currentLoginType = localStorage.getItem(LOGIN_TYPE_KEY);

  const getAuthenticatedType = useCallback(() => {
    if (edgeAuth.isAuthenticated) {
      return LoginType.Edge;
    }
    if (oidcAuth.isAuthenticated) {
      return LoginType.Oidc;
    }
    return LoginType.Unknown;
  }, [edgeAuth.isAuthenticated, oidcAuth.isAuthenticated]);

  const isAuthenticated = useCallback(() => {
    const oidcState = oidcAuth.isAuthenticated;
    const edgeState = edgeAuth.isAuthenticated;

    if (currentLoginType === LoginType.Oidc) {
      return oidcState;
    }
    if (currentLoginType === LoginType.Edge) {
      return edgeState;
    }

    return oidcState || edgeState;
  }, [currentLoginType, edgeAuth.isAuthenticated, oidcAuth.isAuthenticated]);

  const getUser = useMemo(() => {
    if (currentLoginType === LoginType.Oidc) {
      return {
        name: oidcAuth.user?.profile?.given_name || '',
        lastName: oidcAuth.user?.profile?.family_name || '',
        email: oidcAuth.user?.profile?.email || '',
      };
    }

    if (currentLoginType === LoginType.Edge) {
      const token = edgeAuth.getAccessToken();
      if (token?.accessToken) {
        const decodedToken = decodeJwtToken(token.accessToken);
        if (decodedToken) {
          return {
            name: decodedToken.name || '',
            email: decodedToken.email || '',
            lastName: decodedToken.lastName || '',
          };
        }
      }
    }

    return null;
  }, [currentLoginType, oidcAuth.user, edgeAuth]);

  const logout = () => {
    localStorage.removeItem('access_token');
    if (currentLoginType === LoginType.Oidc) {
      return oidcAuth.logout();
    } else if (currentLoginType === LoginType.Edge) {
      edgeAuth.logout();
    }

    localStorage.removeItem(LOGIN_TYPE_KEY);
    return Promise.resolve();
  };

  const login = async (options: {
    username?: string;
    password?: string;
    redirectPath?: string;
  }) => {
    try {
      if (options.username && options.password) {
        await edgeAuth.login(options.username, options.password);
        const token = edgeAuth.getAccessToken();
        if (token?.accessToken) {
          localStorage.setItem('access_token', token.accessToken);
        }
        localStorage.setItem(LOGIN_TYPE_KEY, LoginType.Edge);
      } else {
        await oidcAuth.login(options.redirectPath);
        if (oidcAuth.user?.access_token) {
          localStorage.setItem('access_token', oidcAuth.user.access_token);
        }
        localStorage.setItem(LOGIN_TYPE_KEY, LoginType.Oidc);
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const getAccessToken = useCallback(() => {
    let authenticatedType = getAuthenticatedType();
    if (authenticatedType === LoginType.Edge) {
      let accessToken = edgeAuth.getAccessToken();
      if (!!accessToken && !!accessToken.accessToken) {
        localStorage.setItem('access_token', accessToken.accessToken);
        return accessToken.accessToken;
      }
    } else if (authenticatedType === LoginType.Oidc) {
      if (!!oidcAuth.user?.access_token) {
        localStorage.setItem('access_token', oidcAuth.user.access_token);
        return oidcAuth.user.access_token;
      }
    }
    return '';
  }, [edgeAuth, getAuthenticatedType, oidcAuth.user]);

  return {
    user: getUser,
    isAuthenticated,
    getAuthenticatedType,
    getAccessToken,
    logout,
    login,
  };
}

interface IAuthContext {
  user?: { profile: { name: string; email: string; lastName?: string } } | null;
  isAuthenticated: () => boolean | null;
  login: (options: {
    username?: string;
    password?: string;
    redirectPath?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string;
}

export const AuthContext = createContext<IAuthContext>({
  isAuthenticated: () => null,
  login: (options: { username?: string; password?: string; redirectPath?: string }) =>
    Promise.resolve(),
  logout: () => Promise.resolve(),
  getAccessToken: (): string => '',
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
